import Skeleton from "react-loading-skeleton";
import type { Product } from "~/types";
import { Box } from "../Box/Box";
import { Hidden } from "../Hidden/Hidden";
import { ProductImage } from "../ProductImage/ProductImage";
import { Text } from "../Text/Text";
import type { Color } from "../utils/colors";
import styles from "./MenuItemCard.module.css";

export interface MenuItemCardProps {
  product: Product;
  onSelect(product: Product): void;
}

const disabledStyles = [styles.image, styles.disabled].join(" ");

export function MenuItemCard(props: MenuItemCardProps): React.ReactNode {
  const { product, onSelect } = props;

  const disabled = product && !product?.available;

  // nutritionDescription: e.g. 390-480 cal/slice
  const [description, nutritionDescription] = disabled
    ? ["Currently unavailable"]
    : (product.description || "").trim().split("\n");

  return (
    <Box
      background="white"
      display="flex"
      disabled={disabled}
      border="standard"
      borderColor="neutral-300"
      borderRadius="standard"
      className={styles.menuItemCard}
      onClick={() => !disabled && onSelect(product)}
      flexDirection="column"
      role="button"
      aria-disabled={disabled}
      aria-label={product.title}
    >
      <div
        className={disabled ? disabledStyles : styles.image}
        style={{ lineHeight: 1, flex: "0 0 auto" }}
      >
        {product ? (
          <Box>
            <ProductImage
              src={product.imageUrl}
              alt={product.title}
              contentWidth={540}
            />{" "}
          </Box>
        ) : (
          <Skeleton width={"100%"} height={"100%"} />
        )}
      </div>
      <Hidden above="mobile">
        <MenuItemCardBody
          height={150}
          lines={nutritionDescription ? "3" : "5"}
          title={product.title}
          description={description}
          nutritionDescription={nutritionDescription}
          disabled={disabled}
        />
      </Hidden>
      <Hidden below="tablet">
        <Hidden above="tablet">
          <MenuItemCardBody
            height={145}
            lines={nutritionDescription ? "3" : "5"}
            title={product.title}
            description={description}
            nutritionDescription={nutritionDescription}
            disabled={disabled}
          />
        </Hidden>
      </Hidden>
      <Hidden below="desktop">
        <MenuItemCardBody
          height={140}
          lines={nutritionDescription ? "3" : "5"}
          title={product.title}
          description={description}
          nutritionDescription={nutritionDescription}
          disabled={disabled}
        />
      </Hidden>
    </Box>
  );
}

function MenuItemCardBody(props: {
  height: number;
  truncateTitle?: boolean;
  title: string;
  description: string;
  lines: "2" | "3" | "4" | "5";
  nutritionDescription: string;
  disabled?: boolean;
}) {
  const textColor: Color | undefined = props.disabled
    ? "neutral-400"
    : undefined;

  return (
    <div
      style={{
        height: props.height,
      }}
      className={styles.menuItemCardBody}
    >
      <div>
        <Text
          size="large"
          weight="strong"
          font="accent"
          transform="uppercase"
          truncate={props.truncateTitle}
          clamped={props.truncateTitle ? undefined : "2"}
          color={textColor}
        >
          {props.title}
        </Text>
      </div>
      <div
        style={{
          display: "flex",
          flex: 1,
        }}
      >
        <div>
          <Text clamped={props.lines} priority="low" color={textColor}>
            {props.description}
          </Text>
        </div>
      </div>
      {props.nutritionDescription && (
        <div style={{ flex: 0 }}>
          <Text clamped="1" color={textColor}>
            {props.nutritionDescription}
          </Text>
        </div>
      )}
    </div>
  );
}

export function MenuItemCardSkeleton() {
  return (
    <div style={{ borderRadius: 6 }} aria-hidden="true">
      <div style={{ aspectRatio: "3/2" }}>
        <Skeleton width={"100%"} height={"100%"} />
      </div>
      <Skeleton width={"100%"} height={120} />
    </div>
  );
}
